import axios from "./axiosInstance";
import config from "./config";
import { captureException } from "../utils/sentry";

const BASE_URL = config.BASE_URL;

const authedHeaders = (token: string | null) => ({
  "Content-Type": "application/json",
  authorization: `Bearer ${token}`,
});

const publicHeaders = () => ({
  "Content-Type": "application/json",
});

// ============================================
// TYPES
// ============================================

export interface MembershipUserPermissions {
  view_address: boolean;
  view_other_payment_methods: boolean;
  view_athlete_info: boolean;
  view_billing_history: boolean;
  view_other_contact_info: boolean;
  edit_athletes: boolean;
  edit_membership_settings: boolean;
  edit_payment_methods_own: boolean;
  edit_payment_methods_others: boolean;
  manage_secondary_users: boolean;
}

export interface MembershipUser {
  id: string;
  membership_id: string;
  auth_id: string | null;
  role: "primary" | "secondary";
  status: "invited" | "active" | "revoked";
  invitation_email: string | null;
  invitation_first_name: string | null;
  invitation_last_name: string | null;
  expires_at: string | null;
  invited_by_auth_id: string | null;
  invited_at: string | null;
  accepted_at: string | null;
  revoked_at: string | null;
  stripe_customer_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  permissions: MembershipUserPermissions | null;
}

export interface InvitePayload {
  email: string;
  first_name?: string;
  last_name?: string;
  additional_contact_id?: string;
  permissions?: Partial<MembershipUserPermissions>;
}

export interface InvitationPreview {
  invited_email: string;
  invited_first_name: string;
  invited_last_name: string;
  family_name: string;
  expires_at: string | null;
}

// ============================================
// AUTHENTICATED ENDPOINTS
// ============================================

export const listMembershipUsers = async (
  token: string | null,
  membershipId: string,
  /**
   * `includeRevoked`: also return people whose sign-in was revoked, so a list
   * can say "Sign-in revoked" beside them. The server honors it only for
   * those who can manage sign-ins (staff with the permission, or the primary).
   */
  options: { includeRevoked?: boolean } = {}
): Promise<{ success: boolean; data?: MembershipUser[]; error?: string }> => {
  try {
    const res = await axios.get(`${BASE_URL}/memberships/${membershipId}/users`, {
      headers: authedHeaders(token),
      ...(options.includeRevoked ? { params: { include_revoked: "true" } } : {}),
    });
    return res.data;
  } catch (err: any) {
    captureException(err);
    return {
      success: false,
      error: err?.response?.data?.error || "Failed to load users",
    };
  }
};

export interface MembershipAuditLogEntry {
  id: string;
  action: string;
  actor_role: "admin" | "primary" | "secondary" | "system";
  actor_auth_id: string | null;
  actor_label: string;
  target_membership_user_id: string | null;
  target_label: string | null;
  details: Record<string, any>;
  ip_address: string | null;
  created_at: string;
}

export const listMembershipAuditLog = async (
  token: string | null,
  membershipId: string,
  limit = 100
): Promise<{ success: boolean; data?: MembershipAuditLogEntry[]; error?: string }> => {
  try {
    const res = await axios.get(
      `${BASE_URL}/memberships/${membershipId}/users/audit-log?limit=${limit}`,
      { headers: authedHeaders(token) }
    );
    return res.data;
  } catch (err: any) {
    captureException(err);
    return {
      success: false,
      error: err?.response?.data?.error || "Failed to load audit log",
    };
  }
};

export const inviteMembershipUser = async (
  token: string | null,
  membershipId: string,
  payload: InvitePayload
): Promise<{
  success: boolean;
  /**
   * `email_sent` is false when the invitation was saved but the email did not
   * leave; `message` then says so. Never tell staff "invitation sent" without
   * checking it.
   */
  data?: { membership_user_id: string; expires_at: string; email_sent?: boolean };
  message?: string;
  error?: string;
}> => {
  try {
    const res = await axios.post(
      `${BASE_URL}/memberships/${membershipId}/users/invite`,
      payload,
      { headers: authedHeaders(token) }
    );
    return res.data;
  } catch (err: any) {
    captureException(err);
    return {
      success: false,
      error: err?.response?.data?.error || "Failed to send invitation",
    };
  }
};

export const resendMembershipInvitation = async (
  token: string | null,
  membershipId: string,
  membershipUserId: string
): Promise<{
  success: boolean;
  /** `email_sent` false: the invitation was renewed but the email did not leave. */
  data?: { expires_at?: string; email_sent?: boolean };
  message?: string;
  error?: string;
}> => {
  try {
    const res = await axios.post(
      `${BASE_URL}/memberships/${membershipId}/users/${membershipUserId}/resend-invitation`,
      {},
      { headers: authedHeaders(token) }
    );
    return res.data;
  } catch (err: any) {
    captureException(err);
    return {
      success: false,
      error: err?.response?.data?.error || "Failed to resend invitation",
    };
  }
};

export const revokeMembershipUser = async (
  token: string | null,
  membershipId: string,
  membershipUserId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const res = await axios.delete(
      `${BASE_URL}/memberships/${membershipId}/users/${membershipUserId}`,
      { headers: authedHeaders(token) }
    );
    return res.data;
  } catch (err: any) {
    captureException(err);
    return {
      success: false,
      error: err?.response?.data?.error || "Failed to revoke user",
    };
  }
};

export const promoteMembershipUserToPrimary = async (
  token: string | null,
  membershipId: string,
  membershipUserId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const res = await axios.post(
      `${BASE_URL}/memberships/${membershipId}/users/${membershipUserId}/promote-to-primary`,
      { confirmation: "transfer-primary" },
      { headers: authedHeaders(token) }
    );
    return res.data;
  } catch (err: any) {
    captureException(err);
    return {
      success: false,
      error: err?.response?.data?.error || "Failed to transfer primary management",
    };
  }
};

export const updateMembershipUserPermissions = async (
  token: string | null,
  membershipId: string,
  membershipUserId: string,
  permissions: Partial<MembershipUserPermissions>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const res = await axios.patch(
      `${BASE_URL}/memberships/${membershipId}/users/${membershipUserId}/permissions`,
      permissions,
      { headers: authedHeaders(token) }
    );
    return res.data;
  } catch (err: any) {
    captureException(err);
    return {
      success: false,
      error: err?.response?.data?.error || "Failed to update permissions",
    };
  }
};

// ============================================
// PUBLIC INVITATION FLOW
// ============================================

export const lookupMembershipInvitation = async (
  token: string
): Promise<{ success: boolean; data?: InvitationPreview; error?: string }> => {
  try {
    const res = await axios.get(
      `${BASE_URL}/membership-invitations/${encodeURIComponent(token)}`,
      { headers: publicHeaders() }
    );
    return res.data;
  } catch (err: any) {
    return {
      success: false,
      error: err?.response?.data?.error || "Could not load invitation",
    };
  }
};

export const acceptMembershipInvitation = async (
  token: string,
  body: { password: string; first_name?: string; last_name?: string }
): Promise<{ success: boolean; data?: { membership_id: string; email: string }; error?: string }> => {
  try {
    const res = await axios.post(
      `${BASE_URL}/membership-invitations/${encodeURIComponent(token)}/accept`,
      body,
      { headers: publicHeaders() }
    );
    return res.data;
  } catch (err: any) {
    return {
      success: false,
      error: err?.response?.data?.error || "Failed to accept invitation",
    };
  }
};
